import { Alert, List, Text, Title } from "@mantine/core";
import { generatePath, Link, useParams } from "react-router";
import { COMPONENTS_PANEL_ID } from "../../../../components/StructureView/ComponentsPanel/ComponentsPanel";
import { useGenerationShares } from "../../../../hooks/useGenerationShares";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { ROUTES } from "../../../../routes";
import type { Site } from "../../../../utils/backend-types";
import { ObjectUrlName } from "../../../../utils/enums";
import type { Paragraph6CompanyResponse } from "../../Paragraph6.types";

export interface GenerationShareIncompleteWarningProps {
  site: Site;
  companies: Array<Paragraph6CompanyResponse>;
}

function GenerationShareIncompleteWarning({
  site,
  companies
}: GenerationShareIncompleteWarningProps) {
  const { projectId } = useParams();
  const { generationShares = [] } = useGenerationShares(site.id);
  const companyNames = companies?.map((company) => company.name);

  const generationSharesWithMissingWeighting = generationShares
    ?.filter(
      (share) =>
        share.current_operator && companyNames.includes(share.current_operator)
    )
    ?.filter((share) => share.weighting === null);

  if (
    generationShares.length === 0 ||
    generationSharesWithMissingWeighting.length === 0
  ) {
    return null;
  }

  const showGeneratorList = generationSharesWithMissingWeighting.length > 1;

  return (
    <Alert color={AlertPresets.warning.color} icon={AlertPresets.warning.icon}>
      <Title fw={400} mb="xs" order={4} size="h5">
        Fehlende Daten
      </Title>
      <Text component="div">
        {showGeneratorList ? (
          <List mb="xs">
            {generationSharesWithMissingWeighting.map((share) => (
              <List.Item key={share.generator_id}>
                {share.generator_display_name}
              </List.Item>
            ))}
          </List>
        ) : (
          <b>
            {generationSharesWithMissingWeighting[0].generator_display_name}
          </b>
        )}
        {showGeneratorList ? " haben" : " hat"} keine vollständigen Angaben zum
        Anteil der Erzeugung. Bitte ergänzen Sie die Angaben, bevor Sie mit der
        Vertragseingabe beginnen.
      </Text>
      {projectId && (
        <Link
          to={
            generatePath(ROUTES.siteComponentPage, {
              projectId: projectId,
              siteId: String(site.id),
              objectUrlName: ObjectUrlName.Weightings,
              componentId: "" // No specific component, just open the tab
            }) + `#${COMPONENTS_PANEL_ID}`
          }
        >
          Zu den Erzeugungsanteilen
        </Link>
      )}
    </Alert>
  );
}

export { GenerationShareIncompleteWarning };
